import React, { useContext, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Info } from 'lucide-react';

export default function InvitePlayers() {
  const { user, seasons, API, tenantContext } = useContext(AppContext);
  const navigate = useNavigate();
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('player');
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [welcomeNote, setWelcomeNote] = useState('');
  const [inviting, setInviting] = useState(false);
  const [results, setResults] = useState(null);

  // Check permissions
  const tenantRole = tenantContext?.membership?.role || user?.role;
  const isLeagueAdmin = tenantRole === 'league_admin' || user?.role === 'admin';
  const canInvite = isLeagueAdmin; // Only League Admins can invite

  const handleInvite = async () => {
    const emailList = emails
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (emailList.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }

    // Enforce max 20 invites per season constraint
    if (emailList.length > 20) {
      toast.error('Maximum 20 invites allowed at once');
      return;
    }

    try {
      setInviting(true);
      const response = await authAxios.post(
        `${API}/users/invite`,
        {
          emails: emailList,
          role_default: role,
          seasons: selectedSeasons,
          welcome_note: welcomeNote
        }
      );

      setResults(response.data);
      toast.success(`Invited ${response.data.success.length} users!`);
      
      if (response.data.success.length > 0) {
        setEmails('');
        setWelcomeNote('');
      }
    } catch (error) {
      console.error('Failed to invite users:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invites');
    } finally {
      setInviting(false);
    }
  };

  const toggleSeason = (seasonId) => {
    setSelectedSeasons(prev =>
      prev.includes(seasonId)
        ? prev.filter(id => id !== seasonId)
        : [...prev, seasonId]
    );
  };

  if (!canInvite) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">Only League Admins can invite new players.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button variant="outline" onClick={() => navigate('/admin/players')} className="mb-4">
            ← Back to Players
          </Button>
          <h1 className="text-5xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Invite Players
          </h1>
          <p className="text-lg text-slate-600">Send invitations to join the Survivor Fantasy League</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Invite Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Email Addresses</Label>
              <Textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Enter email addresses (comma or newline separated)&#10;example@email.com, another@email.com"
                rows={5}
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-2">
                Separate multiple emails with commas or line breaks
              </p>
            </div>

            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="league_admin">League Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                <strong>Player:</strong> Can participate in seasons, draft contestants, answer bonus questions<br />
                <strong>Moderator:</strong> Can manage scoring events, bonus questions, and player data<br />
                <strong>League Admin:</strong> Full control over the league and all settings
              </p>
            </div>

            <div>
              <Label>Grant Access to Seasons</Label>
              <div className="mt-2 space-y-2">
                {seasons.length === 0 ? (
                  <p className="text-sm text-slate-500">No seasons available</p>
                ) : (
                  seasons.map(season => (
                    <label key={season.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSeasons.includes(season.id)}
                        onChange={() => toggleSeason(season.id)}
                        className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                      />
                      <span className="text-sm">
                        Season {season.number} {season.title && `- ${season.title}`}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div>
              <Label>Welcome Note (Optional)</Label>
              <Textarea
                value={welcomeNote}
                onChange={(e) => setWelcomeNote(e.target.value)}
                placeholder="Add a personal message to include in the invitation..."
                rows={3}
                className="mt-2"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Note:</h4>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Invited users will receive an email with a link to sign in with Google</li>
                <li>If an email already exists as an active user, the invitation will be skipped</li>
                <li>Deactivated users should be reactivated instead of re-invited</li>
              </ul>
            </div>

            <Button
              onClick={handleInvite}
              disabled={inviting || !emails.trim()}
              className="w-full"
            >
              {inviting ? 'Sending Invitations...' : 'Send Invitations'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <Card>
            <CardHeader>
              <CardTitle>Invitation Results</CardTitle>
            </CardHeader>
            <CardContent>
              {results.success.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-emerald-700 mb-2">
                    Successfully Invited ({results.success.length})
                  </h4>
                  <ul className="text-sm space-y-1">
                    {results.success.map((item, idx) => (
                      <li key={idx} className="text-slate-700">
                        ✓ {item.email} {item.action === 'resent' && '(resent)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.conflicts.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-amber-700 mb-2">
                    Conflicts ({results.conflicts.length})
                  </h4>
                  <ul className="text-sm space-y-1">
                    {results.conflicts.map((item, idx) => (
                      <li key={idx} className="text-slate-700">
                        ⚠ {item.email} - {item.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {results.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2">
                    Errors ({results.errors.length})
                  </h4>
                  <ul className="text-sm space-y-1">
                    {results.errors.map((item, idx) => (
                      <li key={idx} className="text-slate-700">
                        ✗ {item.email} - {item.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
